import { useEffect, useState } from 'react';
import { Alert, Button, Card, Eyebrow, Field, Input, Select } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/** Tenant settings (docs/06 "/settings"). */

interface SettingsPayload {
  timezone: string;
  currency: string;
  currencyLocked: boolean;
  settings: {
    reminderLeadMinutes: number;
    paymentWindowMinutes: number;
    cancellationCutoffHours: number;
    maxSeatsPerBooking: number;
    defaultLocale: '' | 'en' | 'fr' | 'sw' | 'ln';
    supportEmail: string;
    supportPhone: string;
    aboutHtml: string;
    faqHtml: string;
  };
}

const TIMEZONES = [
  'UTC', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Johannesburg', 'Africa/Accra',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid', 'Europe/Istanbul',
  'America/New_York', 'America/Chicago', 'America/Sao_Paulo', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Jakarta', 'Asia/Manila',
];

export function Settings() {
  const t = useT();
  const resource = useResource<SettingsPayload>('/admin/settings');
  const [draft, setDraft] = useState<SettingsPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (resource.data) setDraft(resource.data);
  }, [resource.data]);

  if (!draft) {
    return (
      <Page title={t('nav.settings')}>
        <p style={{ color: 'var(--color-muted)' }}>{resource.error ?? 'Loading…'}</p>
      </Page>
    );
  }

  // Narrowed once here; the closures below capture it rather than re-reading the
  // nullable state variable.
  const current = draft;
  const s = current.settings;
  const set = (patch: Partial<SettingsPayload['settings']>) =>
    setDraft({ ...current, settings: { ...s, ...patch } });

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.put('/admin/settings', {
        timezone: current.timezone,
        ...(current.currencyLocked ? {} : { currency: current.currency }),
        ...s,
      });
      setNotice('Settings saved.');
      await resource.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not save these settings.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page
      title={t('nav.settings')}
      description={t('se.subtitle')}
      actions={
        <Button onClick={save} loading={busy}>
          {t('se.saveSettings')}
        </Button>
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <Eyebrow>{t('se.locale')}</Eyebrow>
          <div className="mt-3 flex flex-col gap-4">
            {/* The operator decides what their passengers open in; the passenger
                can still override it on the Help page. */}
            <Field label={t('se.paxLanguage')} htmlFor="locale" hint={t('se.paxLangHint')}>
              <Select
                id="locale"
                value={s.defaultLocale ?? ''}
                onChange={(e) => set({ defaultLocale: e.target.value as typeof s.defaultLocale })}
              >
                <option value="">{t('se.followDevice')}</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="sw">Kiswahili</option>
                <option value="ln">Lingála</option>
              </Select>
            </Field>
            <Field
              label={t('f.timezone')}
              htmlFor="tz"
              hint={t('se.tzHint')}
            >
              <Select
                id="tz"
                value={current.timezone}
                onChange={(e) => setDraft({ ...current, timezone: e.target.value })}
              >
                {[...new Set([current.timezone, ...TIMEZONES])].map((zone) => (
                  <option key={zone} value={zone}>
                    {zone.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('f.currency')}
              htmlFor="cur"
              hint={
                current.currencyLocked
                  ? 'Locked: passengers have already paid in this currency. Contact platform support to change it.'
                  : 'ISO code, e.g. NGN or EUR. Locks once your first booking is paid for.'
              }
            >
              <Input
                id="cur"
                className="tabular w-28"
                maxLength={3}
                disabled={current.currencyLocked}
                value={current.currency}
                onChange={(e) => setDraft({ ...current, currency: e.target.value.toUpperCase() })}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <Eyebrow>{t('se.policy')}</Eyebrow>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Field
              label={t('se.cutoff')}
              htmlFor="cutoff"
              hint={t('se.cutoffHint')}
            >
              <Input
                id="cutoff"
                type="number"
                min={0}
                max={720}
                value={s.cancellationCutoffHours}
                onChange={(e) => set({ cancellationCutoffHours: Number(e.target.value) })}
              />
            </Field>
            <Field
              label={t('se.payWindow')}
              htmlFor="window"
              hint={t('se.payHint')}
            >
              <Input
                id="window"
                type="number"
                min={5}
                max={120}
                value={s.paymentWindowMinutes}
                onChange={(e) => set({ paymentWindowMinutes: Number(e.target.value) })}
              />
            </Field>
            <Field label={t('se.reminder')} htmlFor="lead" hint={t('se.between')}>
              <Input
                id="lead"
                type="number"
                min={15}
                max={1440}
                value={s.reminderLeadMinutes}
                onChange={(e) => set({ reminderLeadMinutes: Number(e.target.value) })}
              />
            </Field>
            <Field label={t('se.maxSeats')} htmlFor="maxseats">
              <Input
                id="maxseats"
                type="number"
                min={1}
                max={20}
                value={s.maxSeatsPerBooking}
                onChange={(e) => set({ maxSeatsPerBooking: Number(e.target.value) })}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <Eyebrow>{t('se.supportContact')}</Eyebrow>
          <div className="mt-3 flex flex-col gap-4">
            <Field
              label={t('se.supportEmail')}
              htmlFor="semail"
              hint={t('se.supportHint')}
            >
              <Input
                id="semail"
                type="email"
                value={s.supportEmail}
                onChange={(e) => set({ supportEmail: e.target.value })}
              />
            </Field>
            <Field label={t('se.supportPhone')} htmlFor="sphone">
              <Input id="sphone" value={s.supportPhone} onChange={(e) => set({ supportPhone: e.target.value })} />
            </Field>
          </div>
        </Card>

        <Card>
          <Eyebrow>{t('se.helpContent')}</Eyebrow>
          <div className="mt-3 flex flex-col gap-4">
            <Field label={t('se.about')} htmlFor="about">
              <textarea
                id="about"
                className="min-h-24 w-full rounded-lg border p-3 text-sm"
                style={{ borderColor: 'var(--color-line-strong)', background: 'var(--color-bg)' }}
                value={s.aboutHtml}
                onChange={(e) => set({ aboutHtml: e.target.value })}
              />
            </Field>
            <Field label={t('se.faq')} htmlFor="faq">
              <textarea
                id="faq"
                className="min-h-24 w-full rounded-lg border p-3 text-sm"
                style={{ borderColor: 'var(--color-line-strong)', background: 'var(--color-bg)' }}
                value={s.faqHtml}
                onChange={(e) => set({ faqHtml: e.target.value })}
              />
            </Field>
          </div>
        </Card>
      </div>
    </Page>
  );
}
